import type { Analytics } from '@segment/analytics-next'
import { isDevMock } from '../dev/isDevMock'

const configuredKey = import.meta.env.VITE_SEGMENT_WRITE_KEY
const WRITE_KEY = configuredKey === '' ? '' : configuredKey?.trim()

// Segment's runtime fetches workspace settings and injects integration action bundles (e.g.
// /next-integrations/actions/...) as <script> tags that default to cdn.segment.com with no
// integrity attribute — unpinned cross-origin script execution in this origin (QA bug
// bug-mtyvcz3y-29kd, issue #4072). Overriding cdnURL makes the SDK rewrite those URLs to our
// own origin, which serves them through the /segment-cdn/* reverse proxy (netlify.toml and the
// generated dist/_redirects), so no unpinned cross-origin script ever executes in the page.
function segmentCdnUrl(): string {
  return `${window.location.origin}/segment-cdn`
}

// The Segment SDK renders no user-visible UI, so it must not be compiled into the eagerly-loaded
// entry bundle: importing it dynamically makes Vite split it into its own deferred chunk that the
// browser only fetches the first time an analytics call needs a client. Every exported function
// below keeps its synchronous, fire-and-forget signature — nothing in the app awaits analytics.
let analyticsPromise: Promise<Analytics | null> | null = null
let initialized = false

function loadAnalytics(): Promise<Analytics | null> {
  if (!WRITE_KEY || isDevMock()) return Promise.resolve(null)
  if (!analyticsPromise) {
    analyticsPromise = import('@segment/analytics-next')
      .then(({ AnalyticsBrowser }) => AnalyticsBrowser.load({ writeKey: WRITE_KEY, cdnURL: segmentCdnUrl() }))
      .then(([client]) => client)
      .catch((error: unknown) => {
        // A failed load (offline, adblockers) drops the cached attempt so a later call can retry;
        // the rejection is rethrown for the caller's best-effort handler to absorb.
        analyticsPromise = null
        throw error
      })
  }
  return analyticsPromise
}

/** Initialize the browser Segment source (page + identify). */
export function initSegment(): void {
  if (!WRITE_KEY || isDevMock() || initialized) return
  void loadAnalytics().catch(() => {
    // analytics must never throw into the app
  })
  initialized = true
}

export interface SegmentUser {
  id: string
  name: string
  email: string | null
}

/** Attach the signed-in user so Intercom can match server-side events via email. */
export function identifySegmentUser(user: SegmentUser | null): void {
  void loadAnalytics()
    .then((client) => {
      if (!client || !user) return
      void client.identify(user.id, {
        name: user.name,
        ...(user.email ? { email: user.email } : {}),
      })
    })
    .catch(() => {
      // analytics must never throw into the app
    })
}

/** Record a client-side page view (supplements server-side business events). */
export function trackSegmentPage(path: string): void {
  void loadAnalytics()
    .then((client) => {
      if (!client) return
      void client.page({ path })
    })
    .catch(() => {
      // analytics must never throw into the app
    })
}

// Fire a behavioral event to Segment, which forwards it (via the Intercom destination) to the
// user's Intercom contact. This is separate from the internal Umami-style tracker in ./analytics.ts
// (that one only feeds the admin activity feed and never reaches Intercom). Best-effort — never
// throws into the app, no-op when Segment is disabled (no write key / dev mock).
export function trackSegmentEvent(event: string, properties?: Record<string, unknown>): void {
  void loadAnalytics()
    .then((client) => {
      if (!client) return
      void client.track(event, properties ?? {})
    })
    .catch(() => {
      // analytics must never throw into the app
    })
}
